"use client";

import * as React from "react";
import { Loader2Icon } from "lucide-react";

import { cn } from "../../../lib/utils";

/**
 * Phone list behaviour shared by every DataTable: "Charger plus" instead of a pager, pages accumulated in place,
 * and long-press selection. Desktop never renders any of this.
 */

export type DataTableMobileLabels = {
  /** The load-more button. */
  loadMore: string;
  /** "{shown} of {total}" under the list. */
  shownOfTotal: (shown: number, total: number) => string;
  /** Shown once every row is loaded. */
  allLoaded: string;
  /** While the next page is on its way. */
  loading: string;
  /** Sort button label, e.g. "Sort: Name". */
  sortBy: (label: string) => string;
  /** Sort button label when nothing is sorted yet. */
  sort: string;
  /** Leaves selection mode. */
  cancelSelection: string;
  /** Enters selection mode from the ⋯ menu. */
  select: string;
  /** Under "Select" in the ⋯ sheet. */
  selectDescription: string;
  /** Selects every loaded row. */
  selectAll: string;
  /** "{count} selected". */
  selectedCount: (count: number) => string;
  /** The ⋯ menu trigger. */
  moreActions: string;
  /** The clear button inside the phone search field. */
  clearSearch: string;
  /** Screen-reader hint on rows that can be selected. */
  longPressHint: string;
  /** The filter sheet's title, and the back link from a filter's picker. */
  filtersTitle: string;
  /** Applies the filters and closes the sheet. */
  filtersApply: string;
  /** Clears every filter in the sheet (not applied until Apply). */
  filtersReset: string;
  /** An empty date row. */
  filtersChoose: string;
  /** A filter left open ("all"). */
  filtersAny: string;
  /** Default labels of a date range's two rows. */
  filtersFrom: string;
  filtersTo: string;
  /** Default labels of a number range's two rows. */
  filtersMin: string;
  filtersMax: string;
  /** The search field in a filter's picker. */
  filtersSearch: string;
  /** A picker search that matched nothing. */
  filtersNoResults: string;
  /** The row that clears one filter in its picker. */
  filtersClear: string;
  /** A multi-choice filter with several picked, e.g. "3 selected". */
  filtersSelectedCount: (count: number) => string;
};

const DEFAULT_LABELS: DataTableMobileLabels = {
  loadMore: "Load more",
  shownOfTotal: (shown, total) => `${shown} of ${total}`,
  allLoaded: "You've seen everything",
  loading: "Loading…",
  sortBy: (label) => `Sort: ${label}`,
  sort: "Sort",
  cancelSelection: "Cancel",
  select: "Select",
  selectDescription: "Pick several rows to act on them at once",
  selectAll: "All",
  selectedCount: (count) => `${count} selected`,
  moreActions: "More actions",
  clearSearch: "Clear search",
  longPressHint: "Long press to select",
  filtersTitle: "Filters",
  filtersApply: "Apply",
  filtersReset: "Reset",
  filtersChoose: "Choose",
  filtersAny: "All",
  filtersFrom: "From",
  filtersTo: "To",
  filtersMin: "Minimum",
  filtersMax: "Maximum",
  filtersSearch: "Search",
  filtersNoResults: "No results",
  filtersClear: "Clear this filter",
  filtersSelectedCount: (count) => `${count} selected`,
};

const DataTableMobileLabelsContext = React.createContext<DataTableMobileLabels>(DEFAULT_LABELS);

/** Supplies the phone list's strings in the app's language (the package itself only knows English defaults). */
export function DataTableMobileLabelsProvider({
  labels,
  children,
}: {
  labels: Partial<DataTableMobileLabels>;
  children: React.ReactNode;
}) {
  const value = React.useMemo(() => ({ ...DEFAULT_LABELS, ...labels }), [labels]);
  return <DataTableMobileLabelsContext.Provider value={value}>{children}</DataTableMobileLabelsContext.Provider>;
}

export function useDataTableMobileLabels(): DataTableMobileLabels {
  return React.useContext(DataTableMobileLabelsContext);
}

/**
 * Joins the loaded pages into one list: the unbroken run of pages that ends at the current one. A page the list
 * never loaded (for example a link that opened straight on page 3) breaks the run, so nothing stale or out of
 * order is ever shown above it.
 */
export function mergeLoadedPages<T>(pages: ReadonlyMap<number, readonly T[]>, currentPage: number): T[] {
  let start = currentPage;
  while (start > 1 && pages.has(start - 1)) start -= 1;
  const merged: T[] = [];
  for (let page = start; page <= currentPage; page += 1) merged.push(...(pages.get(page) ?? []));
  return merged;
}

/**
 * Keeps each server page's rows as it arrives so a phone list grows instead of being replaced.
 *
 * - Page 1 resets the list: it is a fresh query (first load, or a search, filter, sort or view change).
 * - Rows still identical to the previous page's are ignored: that is the old page lingering while the next one
 *   loads, and storing it would show it twice.
 *
 * Returns the rows to display and the highest page whose own rows have arrived. With no page (`null`) the data
 * passes straight through.
 */
export function useLoadedPages<T>(
  data: T[],
  page: number | null,
  isLoading = false,
): { rows: T[]; loadedPage: number | null } {
  const pagesRef = React.useRef(new Map<number, T[]>());
  return React.useMemo(() => {
    if (page === null) return { rows: data, loadedPage: null };
    return recordLoadedPage(pagesRef.current, page, data, isLoading);
  }, [data, isLoading, page]);
}

/**
 * One step of {@link useLoadedPages}: records `data` as `page` in `pages` (mutating it) and returns what to show.
 * Pure apart from that map, so the rules can be tested without rendering.
 */
export function recordLoadedPage<T>(
  pages: Map<number, T[]>,
  page: number,
  data: T[],
  isLoading = false,
): { rows: T[]; loadedPage: number } {
  // A next page still on its way is not recorded: its placeholder (often an empty list) would replace nothing
  // useful, and the rows already shown must stay put until it lands.
  if (page > 1 && isLoading) {
    return { rows: mergeLoadedPages(pages, page - 1), loadedPage: page - 1 };
  }
  if (page === 1) {
    for (const key of [...pages.keys()]) if (key > 1) pages.delete(key);
  }
  const lingering = page > 1 && pages.get(page - 1) === data;
  if (!lingering) pages.set(page, data);
  return { rows: mergeLoadedPages(pages, page), loadedPage: lingering ? page - 1 : page };
}

/** What the table shares with a server pager rendered inside it, so the pager can turn into "Charger plus". */
export type DataTableMobileListContextValue = {
  /** Whether this list grows with "Load more" (a phone card list that did not opt out). */
  loadMore: boolean;
  /** The pager tells the table which server page it is on. */
  reportServerPage: (page: number) => void;
  /** How many rows the phone list currently shows. */
  shownCount: number;
  /** The highest page whose rows have arrived (the pager shows a spinner until it reaches the requested page). */
  loadedPage: number | null;
  /**
   * Selection mode: entered by a long press on a row or the toolbar's "Select" entry, and left by cancelling
   * (which clears the selection). While on, a tap toggles a row instead of opening it.
   */
  selecting: boolean;
  setSelecting: (selecting: boolean) => void;
};

export const DataTableMobileListContext = React.createContext<DataTableMobileListContextValue | null>(null);

export function useDataTableMobileList(): DataTableMobileListContextValue | null {
  return React.useContext(DataTableMobileListContext);
}

/** The foot of a phone list: count, then "Charger plus" (or a spinner, or "you've seen everything"). */
export function DataTableLoadMore({
  shown,
  total,
  canLoadMore,
  loading,
  onLoadMore,
  className,
}: {
  shown: number;
  total?: number;
  canLoadMore: boolean;
  loading: boolean;
  onLoadMore: () => void;
  className?: string;
}) {
  const labels = useDataTableMobileLabels();
  if (shown === 0) return null;
  return (
    <div className={cn("flex flex-col items-center gap-2 px-1 pt-3 pb-1", className)}>
      {canLoadMore ? (
        <button
          type='button'
          onClick={onLoadMore}
          disabled={loading}
          aria-busy={loading}
          className='flex h-11 w-full items-center justify-center gap-2 rounded-2xl bg-card text-[15px] font-bold text-foreground ring-1 ring-border/60 transition-colors active:bg-muted disabled:opacity-80'
        >
          {loading ? <Loader2Icon aria-hidden className='size-4 animate-spin text-primary' /> : null}
          {loading ? labels.loading : labels.loadMore}
        </button>
      ) : null}
      <p className='text-xs font-semibold text-muted-foreground' aria-live='polite'>
        {typeof total === "number" ? labels.shownOfTotal(shown, total) : null}
        {!canLoadMore && typeof total === "number" && shown >= total && shown > 0 ? ` · ${labels.allLoaded}` : null}
      </p>
    </div>
  );
}

/** How long a press must last before it selects the row. */
export const LONG_PRESS_MS = 450;
/** Movement (px) that turns a press into a scroll and cancels it. */
const LONG_PRESS_SLOP = 10;

/**
 * Long press on a phone row. Returns pointer handlers plus `consumeClick`, which reports (once) that the click
 * following a long press must be swallowed so the row does not also open.
 */
export function useLongPress(onLongPress: (() => void) | undefined) {
  const timerRef = React.useRef<number | null>(null);
  const startRef = React.useRef<{ x: number; y: number } | null>(null);
  const firedRef = React.useRef(false);

  const clear = React.useCallback(() => {
    if (timerRef.current !== null) window.clearTimeout(timerRef.current);
    timerRef.current = null;
    startRef.current = null;
  }, []);

  React.useEffect(() => clear, [clear]);

  const handlers = React.useMemo(
    () =>
      onLongPress
        ? {
            onPointerDown: (event: React.PointerEvent) => {
              if (event.pointerType === "mouse" && event.button !== 0) return;
              // Presses inside a portal opened from the row (a sheet) bubble here too; they are not on the row.
              if (!(event.target instanceof Node) || !event.currentTarget.contains(event.target)) return;
              firedRef.current = false;
              startRef.current = { x: event.clientX, y: event.clientY };
              timerRef.current = window.setTimeout(() => {
                firedRef.current = true;
                timerRef.current = null;
                onLongPress();
              }, LONG_PRESS_MS);
            },
            onPointerMove: (event: React.PointerEvent) => {
              const start = startRef.current;
              if (!start) return;
              if (Math.abs(event.clientX - start.x) > LONG_PRESS_SLOP || Math.abs(event.clientY - start.y) > LONG_PRESS_SLOP) {
                clear();
              }
            },
            onPointerUp: clear,
            onPointerCancel: clear,
            onPointerLeave: clear,
            onContextMenu: (event: React.MouseEvent) => {
              // The browser's own long-press menu would cover the selection.
              if (firedRef.current || timerRef.current !== null) event.preventDefault();
            },
          }
        : {},
    [clear, onLongPress],
  );

  const consumeClick = React.useCallback(() => {
    if (!firedRef.current) return false;
    firedRef.current = false;
    return true;
  }, []);

  return { handlers, consumeClick };
}
